// ============================================
// COMPONENT: CompanySelectView
// Pantalla de selección de empresa post-login
// ============================================
import React from 'react';
import { Activity, Building2, Globe, LogOut } from 'lucide-react';

const ACCENT_COLORS = ['cyan', 'green', 'yellow', 'orange', 'pink', 'indigo'];
const ACCENT_MAP = {
  cyan:   { border: 'border-cyan-500 hover:border-cyan-400',     text: 'text-cyan-400'   },
  green:  { border: 'border-green-500 hover:border-green-400',   text: 'text-green-400'  },
  yellow: { border: 'border-yellow-500 hover:border-yellow-400', text: 'text-yellow-400' },
  orange: { border: 'border-orange-500 hover:border-orange-400', text: 'text-orange-400' },
  pink:   { border: 'border-pink-500 hover:border-pink-400',     text: 'text-pink-400'   },
  indigo: { border: 'border-indigo-500 hover:border-indigo-400', text: 'text-indigo-400' },
};

const CompanySelectView = ({
  user,
  companies = [],
  plants = [],
  equipments = [],
  plantsLoading = false,
  onSelectCompany,
  onLogout,
}) => {
  const countFor = (companyId) => ({
    plants:     plants.filter(p => p.company_id === companyId).length,
    equipments: equipments.filter(e => e.company_id === companyId).length,
  });

  const globalCounts = {
    plants:     plants.length,
    equipments: equipments.length,
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      {/* Nav */}
      <nav className="bg-slate-800 px-6 py-4 border-b border-slate-700 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Activity size={28} className="text-cyan-400" />
          <div>
            <h1 className="text-white font-bold text-lg leading-none">Sistema PRT</h1>
            <p className="text-slate-400 text-xs">{user?.email} · {user?.role}</p>
          </div>
        </div>
        <button
          onClick={onLogout}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm transition-colors"
        >
          <LogOut size={15} />
          Salir
        </button>
      </nav>

      {/* Body */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        <div className="mb-10 text-center">
          <h2 className="text-3xl font-bold text-white mb-2">Selecciona una empresa</h2>
          <p className="text-slate-400">¿Con qué empresa deseas trabajar en esta sesión?</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 w-full max-w-4xl">
          {/* Empresa cards — filtradas a las asignadas si no es Administrador */}
          {(user?.role === 'Administrador'
            ? companies
            : companies.filter(c => (user?.companies || []).includes(c.id))
          ).map((company, idx) => {
            const counts = countFor(company.id);
            const colorKey = ACCENT_COLORS[idx % ACCENT_COLORS.length];
            const { border, text } = ACCENT_MAP[colorKey];

            return (
              <button
                key={company.id}
                onClick={() => onSelectCompany(company.id)}
                className={`group bg-slate-800 border-2 ${border} rounded-xl p-8 flex flex-col items-center gap-4 transition-all duration-200 hover:bg-slate-750 hover:scale-105 focus:outline-none`}
              >
                <div className="p-4 rounded-full bg-slate-700 group-hover:bg-slate-600 transition-colors">
                  <Building2 size={36} className={text} />
                </div>
                <div className="text-center">
                  <p className="text-white font-bold text-xl leading-tight">{company.name}</p>
                </div>
                <div className="w-full border-t border-slate-700 pt-4 grid grid-cols-2 gap-2 text-center">
                  <div>
                    <p className="text-2xl font-bold text-white">
                      {plantsLoading ? '…' : counts.plants}
                    </p>
                    <p className="text-slate-400 text-xs">Plantas</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">
                      {plantsLoading ? '…' : counts.equipments}
                    </p>
                    <p className="text-slate-400 text-xs">Equipos</p>
                  </div>
                </div>
              </button>
            );
          })}

          {/* Vista Global — solo Administrador */}
          {user?.role === 'Administrador' && user?.scope === 'global' && (
            <button
              onClick={() => onSelectCompany(null)}
              className="group bg-slate-800 border-2 border-purple-500 hover:border-purple-400 rounded-xl p-8 flex flex-col items-center gap-4 transition-all duration-200 hover:bg-slate-750 hover:scale-105 focus:outline-none"
            >
              <div className="p-4 rounded-full bg-slate-700 group-hover:bg-slate-600 transition-colors">
                <Globe size={36} className="text-purple-400" />
              </div>
              <div className="text-center">
                <p className="text-white font-bold text-xl leading-tight">Vista Global</p>
                <p className="text-slate-400 text-xs mt-1">Todas las empresas</p>
              </div>
              <div className="w-full border-t border-slate-700 pt-4 grid grid-cols-2 gap-2 text-center">
                <div>
                  <p className="text-2xl font-bold text-white">
                    {plantsLoading ? '…' : globalCounts.plants}
                  </p>
                  <p className="text-slate-400 text-xs">Plantas</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">
                    {plantsLoading ? '…' : globalCounts.equipments}
                  </p>
                  <p className="text-slate-400 text-xs">Equipos</p>
                </div>
              </div>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanySelectView;
